/**
 * What the app does when the person has lost access to a company.
 * The server answers 403/400 with a JSON `code` (company requests and GET /session);
 * a plain 403 without a code (a non-member) is NOT handled here.
 */
import { API_URL } from "../../constants";

/** The membership in the company of the request is switched off (a dismissed employee). */
const MEMBERSHIP_DISABLED = "MEMBERSHIP_DISABLED";
/** Every membership of the person is switched off: nothing to open anywhere. */
const NO_ACTIVE_MEMBERSHIP = "NO_ACTIVE_MEMBERSHIP";
/**
 * Legacy endpoints (such as /vats/*) have no company in the path; they use the company
 * stored on the server session. These two codes say that stored company is off or missing.
 */
const SELECTED_COMPANY_DISABLED = "SELECTED_COMPANY_DISABLED";
const NO_SELECTED_COMPANY = "NO_SELECTED_COMPANY";

function hasErrorCode(error: any, status: number, code: string): boolean {
  return error?.response?.status === status && error.response.data?.code === code;
}

export function isMembershipDisabledError(error: any): boolean {
  return hasErrorCode(error, 403, MEMBERSHIP_DISABLED);
}

export function isNoActiveMembershipError(error: any): boolean {
  return hasErrorCode(error, 403, NO_ACTIVE_MEMBERSHIP);
}

/** A 403 with no code: a non-member, but also what a request without a valid session gets. */
export function isBareForbiddenError(error: any): boolean {
  return error?.response?.status === 403 && !error.response.data?.code;
}

export function isSelectedCompanyProblem(error: any): boolean {
  return (
    hasErrorCode(error, 403, SELECTED_COMPANY_DISABLED) ||
    hasErrorCode(error, 400, NO_SELECTED_COMPANY)
  );
}

/** The company of a page address like /app/<companyId>/..., or null on pages of no company. */
export function getRouteCompanyId(pathname: string): string | null {
  return pathname.match(/^\/app\/([^/]+)/)?.[1] ?? null;
}

/**
 * The first path segment after the API address: the company id in
 * <API_URL>/<companyId>/auth/role. For <API_URL>/vats/fetch it is "vats", which
 * never equals a company id.
 */
function getRequestCompanyId(requestUrl: string | undefined): string | null {
  if (!requestUrl?.startsWith(API_URL)) return null;
  return requestUrl.slice(API_URL.length).match(/^\/([^/?#]+)(?:[/?#]|$)/)?.[1] ?? null;
}

/**
 * True for a MEMBERSHIP_DISABLED answer to a request about the company the person
 * has open. Another company's request (a widget, a link) says nothing about this page.
 */
export function isDisabledCompanyOfThisPage(error: any): boolean {
  if (!isMembershipDisabledError(error)) return false;
  const routeCompanyId = getRouteCompanyId(window.location.pathname);
  return routeCompanyId !== null && getRequestCompanyId(error.config?.url) === routeCompanyId;
}

/**
 * Why the company list shows a notice:
 * - "removed": the server said the membership in the company is switched off;
 * - "none": the app found no access to the company in the person's lists (a dismissed
 *   member and a person who never belonged look the same from here).
 */
export type AccessLossReason = "removed" | "none";

export function pickerUrlWithNotice(reason: AccessLossReason): string {
  return `/app?access=${reason}`;
}

export function getAccessLossReason(search: string): AccessLossReason | null {
  const reason = new URLSearchParams(search).get("access");
  return reason === "removed" || reason === "none" ? reason : null;
}

let isLeavingCompany = false;

/**
 * Takes the person out of a company they cannot use, to the company list with the
 * notice for the reason. The session stays: they may work in other companies.
 *
 * It is also called from the axios interceptor, outside React, so it reloads the page
 * (this also drops the stale company list). Many requests of one page fail at once
 * and each one calls this, so only the first call navigates.
 */
export function leaveCompany(reason: AccessLossReason): void {
  if (isLeavingCompany) return;

  isLeavingCompany = true;
  window.location.assign(pickerUrlWithNotice(reason));
}
